/**
 * Écran « vous n'avez pas de siège » (mode `registre`, lot O3).
 *
 * Membre actif d'une organisation du studio, mais sans siège exiPlan : le serveur répond 403
 * `SEAT_REQUIRED` aux collections de l'organisation et 404 à ses éléments, et le résumé
 * (`GET /organizations/:id`) porte `siege: false`. Cet écran explique la situation et offre le
 * seul geste utile, « Demander un siège » (`POST …/demandes-siege`, idempotent côté serveur :
 * une demande ouverte par membre).
 *
 * Le bouton primaire vit dans l'emplacement `primary` de `PageHeader` (règle de cohérence 1.3.0
 * d'`@exidis/ui`). Registre dégradé (`degrade: true`) : lecture seule, la demande est refusée
 * d'avance plutôt que d'attendre un 503. Les rôles de gestion sans siège (un `facturation` n'a
 * jamais d'auto-siège, AUTO-4) reçoivent en plus, par `children`, de quoi gérer les sièges.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { messageModeleOrganisation } from '../lib/org-errors.js';
import { PageBody, PageHeader, PrimaryButton } from '../components/common.jsx';
import { CELLS_PLANCHE, CELL_PLANCHE } from '../ui/theme.js';

/** Littéraux dans l'objet — le gate `keys.test.js` ne voit pas une clé de gabarit calculée. */
const ROLE_LABEL_KEYS = {
  proprietaire: 'roleBadge.proprietaire',
  administrateur: 'roleBadge.administrateur',
  facturation: 'roleBadge.facturation',
  editeur: 'roleBadge.editeur',
  lecteur: 'roleBadge.lecteur',
};

/**
 * @param {object} props
 * @param {{ id: string, name: string, role: string, statut_siege?: string|null }} props.organization
 *   Résumé servi par `GET /organizations/:id` en mode `registre`.
 * @param {boolean} [props.degrade] registre indisponible : lecture seule
 * @param {import('react').ReactNode} [props.children] gestion des sièges (rôles de gestion)
 */
export function SeatRequiredPage({ organization, degrade = false, children }) {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const suspendu = organization.statut_siege === 'suspendu';
  const roleKey = ROLE_LABEL_KEYS[organization.role];

  const request = async () => {
    setSending(true);
    setError(null);
    try {
      await api.post(`/organizations/${organization.id}/demandes-siege`);
      setSent(true);
    } catch (failure) {
      setError(
        messageModeleOrganisation(failure, t) ??
          t('seatRequired.requestFailed', { message: failure.message }),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow={t('seatRequired.eyebrow')}
        title={t('seatRequired.title')}
        primary={
          <PrimaryButton disabled={sending || sent || degrade} onClick={request}>
            {t('seatRequired.request')}
          </PrimaryButton>
        }
      />
      <PageBody className="p-4">
        <div className="flex flex-col gap-4">
          <div className={`${CELLS_PLANCHE} grid-cols-1`}>
            <div className={`${CELL_PLANCHE} flex flex-col gap-3 bg-paper p-6`}>
              <span className="ticks" aria-hidden="true" />
              <p className="text-sm text-ink">
                {suspendu
                  ? t('seatRequired.introSuspended', { name: organization.name })
                  : t('seatRequired.intro', {
                      name: organization.name,
                      role: roleKey ? t(roleKey) : organization.role,
                    })}
              </p>
              <p className="text-xs text-ink-faint">{t('seatRequired.yourSpace')}</p>
              {degrade && (
                <p role="status" className="text-sm text-accent-ink">
                  {t('seatRequired.degraded')}
                </p>
              )}
              {sent && (
                <p role="status" className="text-sm text-ink">
                  {t('seatRequired.sent')}
                </p>
              )}
              {error && (
                <p role="alert" className="text-sm text-accent-ink">
                  {error}
                </p>
              )}
            </div>
          </div>
          {children}
        </div>
      </PageBody>
    </div>
  );
}
